import {useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {processingTranscriptionsSelector} from '../store/selectors/transcription.selectors';
import {pollTranscriptionStatus} from '../store/slices/transcriptionsThunks';
import {fetchTranscriptionUsage} from '../store/slices/usageThunks';
import {useToast} from './Toast';

/** The backend suggests 3–5s; 4s is often enough to catch a short clip on its first or second poll. */
const POLL_INTERVAL_MS = 4000;

/**
 * Watches every running transcription, app-wide.
 *
 * Mounted once in the protected layout rather than on the transcriptions page, because the job
 * outlives the page that started it: the user is explicitly told they can navigate away, and a
 * poller that stopped when they did would leave rows stuck at "processing" until a manual refresh.
 * Renders nothing — the rows in the list are the UI.
 */
const TranscriptionPollerContainer = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const processing = useAppSelector(processingTranscriptionsSelector);

  // Ids currently being polled, so a slow response cannot stack a second request on top.
  const inFlightRef = useRef<Set<string>>(new Set());

  const processingIds = processing.map(t => t.id).join(',');

  useEffect(() => {
    if (!processingIds) {
      return;
    }

    const ids = processingIds.split(',');

    const poll = () => {
      ids.forEach(async (id) => {
        if (inFlightRef.current.has(id)) {
          return;
        }

        inFlightRef.current.add(id);

        try {
          const result = await dispatch(pollTranscriptionStatus(id));

          if (!pollTranscriptionStatus.fulfilled.match(result)) {
            // Transient failures are left to the next tick — a toast per failed poll would be a
            // toast every four seconds on a flaky connection.
            return;
          }

          if (result.payload.status === 'processing') {
            return;
          }

          // Both meters move when a job settles, and not only downwards: a failed job refunds its
          // period *and* its session seconds, so the balance can go up on its own. That is why this
          // re-reads the server rather than adjusting a local counter — a decrement would leave the
          // header claiming time the user has actually just been given back. A completed job also
          // settles at AssemblyAI's duration, which is rarely exactly what we reserved.
          dispatch(fetchTranscriptionUsage());

          if (result.payload.status === 'completed') {
            showToast(`${result.payload.name} finished transcribing`, 'success');
          } else if (result.payload.status === 'failed') {
            showToast(
              `${result.payload.name} failed: ${result.payload.error || 'no reason given'}`,
              'error',
              8000
            );
          } else if (result.payload.status === 'rejected') {
            // Distinct from 'failed': no CSV was ever produced and, unlike a failure, these seconds
            // are not refunded — the balance fetch above already reflects that.
            showToast(
              `${result.payload.name} was rejected: ${result.payload.error || 'no reason given'}`,
              'error',
              8000
            );
          }
        } finally {
          inFlightRef.current.delete(id);
        }
      });
    };

    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [processingIds, dispatch, showToast]);

  return null;
};

export default TranscriptionPollerContainer;
